import { useEffect, useRef, useState } from "react";
import { adminApi, wsUrl } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Send, MessageSquare } from "lucide-react";

export default function AdminChat() {
  const { admin } = useAdminAuth();
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const wsRef = useRef(null);
  const bottomRef = useRef(null);

  const loadConversations = async () => {
    const { data } = await adminApi.get("/admin/chat/conversations");
    setConversations(data);
  };

  useEffect(() => { loadConversations(); }, []);

  useEffect(() => {
    if (!active) return;
    (async () => {
      const { data } = await adminApi.get(`/chat/conversations/${active.id}/messages`);
      setMessages(data);
    })();
    const ws = new WebSocket(wsUrl(`/ws/chat/${active.id}`));
    ws.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      loadConversations();
    };
    wsRef.current = ws;
    return () => ws.close();
  }, [active]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = () => {
    const t = text.trim();
    if (!t || !wsRef.current || wsRef.current.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({ sender: "admin", sender_name: admin?.name || "Idea Events", text: t }));
    setText("");
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="overline-label mb-1">Concierge</p>
        <h1 className="font-serif text-3xl text-cream">Live Chat Inbox</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[70vh]">
        <div data-testid="admin-chat-conversation-list" className="gold-card !border-gold/20 p-3 overflow-y-auto">
          {conversations.length === 0 && <p className="text-muted2 text-sm p-4 text-center">No conversations yet.</p>}
          {conversations.map((c) => (
            <button key={c.id} data-testid={`admin-chat-conversation-${c.id}`} onClick={() => setActive(c)}
              className={`w-full text-left p-3 rounded-lg mb-1 transition-colors ${active?.id === c.id ? "bg-gold-deep border border-gold/40" : "hover:bg-ink-700"}`}>
              <div className="flex items-center justify-between">
                <span className="text-cream text-sm truncate">{c.customer_name || c.customer_email}</span>
                {c.unread_admin && <span className="w-2 h-2 rounded-full bg-gold shrink-0" />}
              </div>
              <p className="text-xs text-muted2 truncate mt-1">{c.last_message || "New conversation"}</p>
            </button>
          ))}
        </div>

        <div className="md:col-span-2 gold-card !border-gold/20 flex flex-col overflow-hidden">
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-muted2">
              <MessageSquare className="w-10 h-10 mb-3 text-gold/40" />
              <p>Select a conversation to reply</p>
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-gold/20">
                <p className="font-serif text-cream">{active.customer_name || active.customer_email}</p>
                <p className="text-xs text-muted2">{active.customer_email}</p>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-3 py-2 rounded-xl text-sm ${m.sender === "admin" ? "bg-gold text-ink" : "bg-ink-700 text-cream border border-gold/20"}`}>
                      {m.text}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <div className="p-3 border-t border-gold/20 flex gap-2">
                <input data-testid="admin-chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Type your reply…" className="flex-1 bg-ink border border-gold/25 rounded-full px-4 py-2 text-sm text-cream focus:outline-none focus:border-gold" />
                <button data-testid="admin-chat-send-message-button" onClick={send} className="btn-gold !p-0 w-10 h-10 rounded-full"><Send className="w-4 h-4" /></button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
